import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { getStoredDemoChapter } from '../utils/demoNavContext';
import LogoWithBeta from './LogoWithBeta';
import { LOGO_HEIGHT_SIDEBAR } from './logoMarkSizes';
import DemoNotificationsPanel from './DemoNotificationsPanel';
import {
  CHAPTER_NAV,
  SHARED_NAV,
  CHAPTER_KEYS,
  isDemoChapterComingSoon,
  demoSlugToChapterKey,
  getChapterKeyFromPath,
  getChapterKeyFromJourneyType,
  demoHref,
  demoSupportHref,
  appPathForSegment,
} from '../data/dashboardNavConfig';

const FONT_BODY = "'Plus Jakarta Sans', -apple-system, sans-serif";

const SECTION_HEADING = {
  fontSize: 10,
  fontWeight: 600,
  letterSpacing: '0.1em',
  color: '#5f5e5a',
  textTransform: 'uppercase',
  paddingLeft: 12,
  paddingRight: 12,
  marginBottom: 4,
  fontFamily: FONT_BODY,
};

function ChevronDown({ size = 14, color = 'currentColor' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden style={{ flexShrink: 0 }}>
      <path d="M6 9l6 6 6-6" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function isDemoNavItemActive(pathname, segment, inDemo) {
  if (!inDemo || !segment) return false;
  const m = pathname.match(/^\/demo\/[^/]+\/(.+)$/);
  const rest = m ? m[1] : '';
  const base = rest.split('/')[0];
  return base === segment || rest === segment;
}

function isAppNavItemActive(pathname, href) {
  if (href === '/') return pathname === '/';
  return pathname === href || pathname.startsWith(`${href}/`);
}

function NavItemButton({ label, icon, active, activeColor, activeBg, onClick }) {
  const [hover, setHover] = useState(false);
  const inactiveColor = '#5F5E5A';
  return (
    <button
      type="button"
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        width: '100%',
        textAlign: 'left',
        padding: '8px 12px',
        fontSize: 13,
        fontWeight: active ? 600 : 500,
        fontFamily: FONT_BODY,
        color: active ? activeColor : inactiveColor,
        background: active ? activeBg : hover ? '#F5F0E8' : 'transparent',
        border: 'none',
        borderRadius: 8,
        cursor: 'pointer',
        transition: 'background 0.15s ease, color 0.15s ease',
      }}
    >
      <span style={{ fontSize: 16, lineHeight: 1 }}>{icon}</span>
      {label}
    </button>
  );
}

const DEMO_PROFILE_ROUTE_RE = /^\/demo\/(fertility|trying-to-conceive)\/profile\/?$/;

export default function DashboardNav({ embedded = false }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useApp();

  const demoMatch = location.pathname.match(/^\/demo\/([^/]+)(?:\/(.+))?/);
  const novaPath = location.pathname === '/nova' || location.pathname === '/nova-chat';
  const storedDemoChapter = typeof window !== 'undefined' ? getStoredDemoChapter() : null;
  const inDemo = !!demoMatch || (!!novaPath && !!storedDemoChapter);
  const demoSlugFromUrl = demoMatch?.[1] || (novaPath && storedDemoChapter ? storedDemoChapter : null);

  const pathChapterKey = getChapterKeyFromPath(location.pathname);
  const journeyChapterKey = getChapterKeyFromJourneyType(user?.journeyType || user?.journey_type);
  const activeChapterKey = inDemo
    ? (pathChapterKey || (demoSlugFromUrl ? demoSlugToChapterKey(demoSlugFromUrl) : 'fertility'))
    : journeyChapterKey;

  const activeChapter = CHAPTER_NAV[activeChapterKey] || CHAPTER_NAV.fertility;
  const effectiveDemoSlug = inDemo ? (demoSlugFromUrl || activeChapter.demoSlug) : activeChapter.demoSlug;

  const [chapterSwitcherOpen, setChapterSwitcherOpen] = useState(false);
  const switcherRef = useRef(null);
  const bellRef = useRef(null);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [notifCount] = useState(3);

  const showDemoFtProfileRow =
    inDemo && (effectiveDemoSlug === 'fertility' || effectiveDemoSlug === 'trying-to-conceive');
  const isDemoProfileRouteActive = DEMO_PROFILE_ROUTE_RE.test(location.pathname);
  const profilePath = user?.email ? `/profile/${encodeURIComponent(user.email)}` : null;

  const demoProfileHref =
    showDemoFtProfileRow ? `/demo/${effectiveDemoSlug}/profile` : null;

  const goSidebarProfile = () => {
    if (demoProfileHref) {
      navigate(demoProfileHref);
      return;
    }
    navigate(profilePath || '/signup?source=dashboard-sidebar');
  };

  useEffect(() => {
    if (!chapterSwitcherOpen) return undefined;
    const onDoc = (e) => {
      if (switcherRef.current && !switcherRef.current.contains(e.target)) {
        setChapterSwitcherOpen(false);
      }
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setChapterSwitcherOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [chapterSwitcherOpen]);

  const resolveHref = (segment) => {
    if (inDemo) {
      if (segment === 'community' || segment === 'connect') {
        if (activeChapterKey === 'ttc') {
          return demoHref(effectiveDemoSlug, segment);
        }
        return demoSupportHref(segment);
      }
      return demoHref(effectiveDemoSlug, segment);
    }
    return appPathForSegment(segment);
  };

  const navButton = (segment, label, icon) => {
    const href = resolveHref(segment);
    const active = inDemo
      ? isDemoNavItemActive(location.pathname, segment, inDemo)
      : isAppNavItemActive(location.pathname, href);
    return (
      <NavItemButton
        key={`${segment}-${label}`}
        label={label}
        icon={icon}
        active={active}
        activeColor={activeChapter.navActiveText || activeChapter.color}
        activeBg={activeChapter.colorLight}
        onClick={() => navigate(href)}
      />
    );
  };

  const onChapterSelect = (key) => {
    if (isDemoChapterComingSoon(key)) return;
    const ch = CHAPTER_NAV[key];
    if (!ch) return;
    setChapterSwitcherOpen(false);
    navigate(demoHref(ch.demoSlug, 'home'));
  };

  const logoNavigate = () => {
    if (inDemo) {
      navigate('/');
      return;
    }
    if (demoMatch) navigate(`/demo/${demoMatch[1]}/home`);
    else if (novaPath && storedDemoChapter) navigate(`/demo/${storedDemoChapter}/home`);
    else navigate('/');
  };

  const displayName =
    showDemoFtProfileRow && !user ? 'Maya' : user?.first_name || user?.name?.split(/\s+/)[0] || 'Your profile';
  const displayInitial =
    showDemoFtProfileRow && !user
      ? 'M'
      : (user?.first_name?.[0] || user?.name?.[0] || '?').toUpperCase();

  return (
    <aside
      style={{
        width: 220,
        minHeight: embedded ? 0 : '100vh',
        background: 'var(--white)',
        borderRight: embedded ? 'none' : '1px solid var(--border)',
        display: 'flex',
        flexDirection: 'column',
        position: embedded ? 'relative' : 'sticky',
        top: embedded ? undefined : 0,
        height: embedded ? 'auto' : '100vh',
        boxSizing: 'border-box',
      }}
    >
      <DemoNotificationsPanel
        open={notificationsOpen}
        onClose={() => setNotificationsOpen(false)}
        bellRef={bellRef}
      />

      {/* Logo + chapter switcher */}
      <div
        style={{
          flexShrink: 0,
          padding: '16px 12px 10px',
          borderBottom: '0.5px solid var(--sand-2, #ede8df)',
          marginBottom: 0,
        }}
      >
        <div onClick={logoNavigate} role="presentation">
          <LogoWithBeta
            height={LOGO_HEIGHT_SIDEBAR}
            style={{
              width: '100%',
              boxSizing: 'border-box',
              padding: '0 0 8px',
              cursor: 'pointer',
            }}
          />
        </div>

        {inDemo && (
          <div ref={switcherRef} style={{ position: 'relative', zIndex: 20 }}>
            <button
              type="button"
              aria-expanded={chapterSwitcherOpen}
              aria-controls="demo-chapter-switcher-list"
              onClick={() => setChapterSwitcherOpen((o) => !o)}
              style={{
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '8px 12px',
                borderRadius: 12,
                border: 'none',
                cursor: 'pointer',
                fontSize: 13,
                fontWeight: 600,
                fontFamily: FONT_BODY,
                backgroundColor: activeChapter.colorLight,
                color: activeChapter.color,
              }}
            >
              <span>
                {activeChapter.emoji} {activeChapter.label}
              </span>
              <ChevronDown size={14} color={activeChapter.color} />
            </button>
            {chapterSwitcherOpen && (
              <div
                id="demo-chapter-switcher-list"
                role="group"
                aria-label="Demo chapters"
                style={{
                  marginTop: 4,
                  borderRadius: 12,
                  border: '1px solid #E8E6DF',
                  background: 'white',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                  overflow: 'hidden',
                }}
              >
                {CHAPTER_KEYS.map((key) => {
                  const ch = CHAPTER_NAV[key];
                  const comingSoon = isDemoChapterComingSoon(key);
                  const rowStyle = {
                    width: '100%',
                    minWidth: 0,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                    padding: '8px 10px',
                    border: 'none',
                    background: 'white',
                    textAlign: 'left',
                    fontSize: 13,
                    fontFamily: FONT_BODY,
                  };
                  if (comingSoon) {
                    return (
                      <button
                        key={key}
                        type="button"
                        disabled
                        aria-label={`${ch.label}, coming in 2026`}
                        style={{
                          ...rowStyle,
                          cursor: 'not-allowed',
                          opacity: 0.65,
                          color: '#5f5e5a',
                        }}
                      >
                        <span aria-hidden="true" style={{ flexShrink: 0 }}>
                          {ch.emoji}
                        </span>
                        <span
                          style={{
                            flex: 1,
                            minWidth: 0,
                            color: '#3d3d3a',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                          aria-hidden="true"
                        >
                          {ch.label}
                        </span>
                        <span
                          aria-hidden="true"
                          style={{
                            flexShrink: 0,
                            fontSize: 10,
                            fontWeight: 600,
                            letterSpacing: '0.02em',
                            color: '#8a8883',
                            whiteSpace: 'nowrap',
                            lineHeight: 1.2,
                          }}
                        >
                          Coming 2026
                        </span>
                      </button>
                    );
                  }
                  return (
                    <button
                      key={key}
                      type="button"
                      onClick={() => onChapterSelect(key)}
                      style={{
                        ...rowStyle,
                        cursor: 'pointer',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = '#F5F0E8';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'white';
                      }}
                    >
                      <span>{ch.emoji}</span>
                      <span style={{ color: '#3d3d3a' }}>{ch.label}</span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Scrollable nav sections */}
      <div
        style={{
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          padding: '8px 0',
        }}
      >
        {activeChapter.sections.map((section) => (
          <div key={section.heading} style={{ marginBottom: 16 }}>
            <div style={SECTION_HEADING}>{section.heading}</div>
            {section.items.map((item) => navButton(item.segment, item.label, item.icon))}
          </div>
        ))}

        {!['ttc', 'fertility'].includes(activeChapterKey) && (
          <div
            style={{
              height: 1,
              background: '#E8E6DF',
              margin: '8px 12px 16px',
            }}
          />
        )}

        {!['ttc', 'fertility'].includes(activeChapterKey) &&
          SHARED_NAV.map((block) => (
            <div key={block.heading} style={{ marginBottom: 16 }}>
              <div style={SECTION_HEADING}>{block.heading}</div>
              {block.items.map((item) => navButton(item.segment, item.label, item.icon))}
            </div>
          ))}
      </div>

      {/* Profile row — fertility/TTC demo: pink→sage gradient bar (matches chapter shell headers) */}
      <div
        style={{
          flexShrink: 0,
          borderTop: showDemoFtProfileRow ? '1px solid #d18e9b' : '0.5px solid var(--sand-2, #ede8df)',
          padding: '12px 12px',
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          background: showDemoFtProfileRow
            ? 'linear-gradient(140deg, var(--pink-l, #fbeaf0), var(--sage-l, #e1f5ee))'
            : 'transparent',
          boxShadow: showDemoFtProfileRow
            ? isDemoProfileRouteActive
              ? '0 -2px 14px rgba(17, 17, 17, 0.06), inset 0 0 0 1px rgba(212, 83, 126, 0.22)'
              : '0 -2px 14px rgba(17, 17, 17, 0.05)'
            : undefined,
        }}
      >
        <div
          role="button"
          tabIndex={0}
          onClick={goSidebarProfile}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              goSidebarProfile();
            }
          }}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            flex: 1,
            minWidth: 0,
            cursor: 'pointer',
          }}
        >
          <div
            style={{
              width: 28,
              height: 28,
              borderRadius: '50%',
              background: 'var(--pink-l, #fbeaf0)',
              color: 'var(--pink-d, #993556)',
              fontFamily: FONT_BODY,
              fontSize: 11,
              fontWeight: 700,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            {displayInitial}
          </div>
          <span
            style={{
              fontSize: 12,
              fontWeight: 600,
              color: 'var(--text, #111)',
              flex: 1,
              minWidth: 0,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {displayName}
          </span>
        </div>
        {showDemoFtProfileRow ? (
          <button
            ref={bellRef}
            type="button"
            aria-label="Notifications"
            onClick={(e) => {
              e.stopPropagation();
              setNotificationsOpen((o) => !o);
            }}
            style={{
              width: 26,
              height: 26,
              borderRadius: 6,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              position: 'relative',
              background: 'transparent',
              border: 'none',
              padding: 0,
            }}
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden>
              <path
                d="M8 1.5a4.5 4.5 0 0 0-4.5 4.5v2L2 10h12l-1.5-2V6A4.5 4.5 0 0 0 8 1.5Z"
                stroke="#7a6b63"
                strokeWidth="1.2"
              />
              <path d="M6.5 10.5a1.5 1.5 0 0 0 3 0" stroke="#7a6b63" strokeWidth="1.2" />
            </svg>
            {notifCount > 0 ? (
              <span
                style={{
                  position: 'absolute',
                  top: -2,
                  right: -2,
                  width: 14,
                  height: 14,
                  borderRadius: 7,
                  background: 'var(--pink, #d4537e)',
                  border: '1.5px solid var(--white, #fffcf9)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 8,
                  fontWeight: 700,
                  color: 'white',
                }}
              >
                {notifCount}
              </span>
            ) : null}
          </button>
        ) : null}
      </div>
    </aside>
  );
}
